import type { MyBusiness } from "@teamna/shared";
import { ButtonLink, InlineError } from "@teamna/ui/components/index.ts";
import { SuggestionsCard, type SuggestionsReadOnly } from "@teamna/ui/owner/assistant/suggestions-card.tsx";
import { OwnerHomeScreen } from "@teamna/ui/owner/home.tsx";
import { OfficePage } from "@teamna/ui/owner/office.tsx";
import { useAssistantCards } from "@teamna/web-client/hooks/use-assistant.ts";
import { useActingAs } from "../lib/act-as.ts";
import { AiAssistantCard } from "./ai-assistant-card.tsx";

/**
 * AI Assistant while acting as a business (ai-assistant-v1.md, "Control Center"). A chat belongs to
 * the person who asked, so act-as never opens one and never asks a question. Teamna staff see what
 * the owner sees around the chats: the package, and the suggestions of Home, read only. The chats
 * themselves are in AI Assistant review, where each open writes an audit row with the staff email.
 */

/** The links from the act-as screens to AI Assistant review of the business. */
export function reviewLinks(businessId: string): SuggestionsReadOnly {
  return {
    chatHref: (conversationId) => `/assistant/${conversationId}`,
    reviewHref: `/assistant?business=${businessId}`,
  };
}

/** `/owner/home` while acting: the Desk Home, with the suggestions read only. */
export function ActAsHomeScreen() {
  const acting = useActingAs();
  return <OwnerHomeScreen suggestionsReadOnly={acting ? reviewLinks(acting.businessId) : undefined} />;
}

/** `/owner/assistant` while acting. */
export function ActAsAssistantScreen() {
  return (
    <OfficePage
      title="AI Assistant"
      description="The AI Assistant package and suggestions of this business. You cannot ask a question or open a chat while you act as the business."
    >
      {(business) => <ActAsAssistantBody business={business} />}
    </OfficePage>
  );
}

function ActAsAssistantBody({ business }: { business: MyBusiness }) {
  const links = reviewLinks(business.businessId);
  const on = business.aiAssistant === true;
  const cards = useAssistantCards(on ? business.businessId : null);

  return (
    <div>
      <AiAssistantCard businessId={business.businessId} timezone={business.timezone} />

      {on ? (
        <div className="mb-8">
          {cards.error ? (
            <InlineError error={cards.error} />
          ) : cards.data?.length === 0 ? (
            <p className="text-[14px] text-ink-2">AI Assistant has no suggestions for this business now.</p>
          ) : (
            <SuggestionsCard business={business} readOnly={links} />
          )}
        </div>
      ) : null}

      <section aria-labelledby="act-as-chats-heading" className="rounded-card border border-line bg-paper p-5 shadow-card">
        <h2 id="act-as-chats-heading" className="mb-2 text-[17px]">
          Chats
        </h2>
        <p className="mb-4 text-[14px] text-ink-2">
          Each chat belongs to the person who asked. Read the chats of this business in AI Assistant review. Each chat that you open there is recorded with your email.
        </p>
        <ButtonLink variant="secondary" href={links.reviewHref} target="_blank" rel="noreferrer">
          Open AI Assistant review
        </ButtonLink>
      </section>
    </div>
  );
}
